/**
 * User API schemas — SINGLE SOURCE OF TRUTH for user update validation.
 *
 * JSON array fields (personalValues, interests, causes, preferredDealTypes,
 * preferredIndustries) are stored as JSON strings in the DB but presented as
 * arrays at the API boundary.
 *
 * @package @app/validators
 */

import { z } from 'zod';

/**
 * Social media handle (without leading @). Allows letters, numbers, dots,
 * underscores, hyphens. Mirrors `socialHandleSchema` from the package root.
 */
const socialHandleSchema = z
  .string()
  .regex(
    /^[a-zA-Z0-9._-]{1,30}$/,
    'Invalid social handle (letters, numbers, dots, underscores, hyphens)',
  );

/**
 * US-031: NCAA Eligibility ID — exactly 10 digits per the NCAA Eligibility
 * Center's format. Stripped to digits before validation so callers can pass
 * `"123-456-7890"` or `"123 456 7890"` or `"1234567890"` interchangeably.
 * Use {@link normalizeNcaaId} to format for display.
 */
export const ncaaIdSchema = z
  .string()
  .transform((s) => s.replace(/\D/g, ''))
  .refine((s) => s === '' || /^\d{10}$/.test(s), { message: 'NCAA ID is 10 digits' });

/** Strip an NCAA ID to digits only — what we persist. */
export function normalizeNcaaId(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const digits = raw.replace(/\D/g, '');
  return digits === '' ? null : digits;
}

/** Format a stored NCAA ID for human-friendly display. Currently a no-op
 * (the Eligibility Center renders the ID as a single 10-digit run); kept
 * as a separate function so a future "1234-567-890" style is one edit. */
export function formatNcaaId(stored: string | null | undefined): string {
  if (!stored) return '';
  return stored;
}

export const userUpdateInputSchema = z.object({
  // Legacy
  name: z.string().min(1).max(200).optional(),
  image: z.string().url().optional(),
  sport: z.string().max(100).optional(),
  school: z.string().max(200).optional(),
  socialFollowers: z.number().int().optional(),
  socialEngagement: z.number().optional(),
  phoneNumber: z.string().optional(),
  language: z.string().optional(),
  // Athletic info
  position: z.string().max(50).nullable().optional(),
  classYear: z.string().nullable().optional(),
  conference: z.string().max(50).nullable().optional(),
  division: z.string().nullable().optional(),
  hometown: z.string().max(100).nullable().optional(),
  // Personal brand
  bio: z.string().max(500).nullable().optional(),
  personalValues: z.array(z.string().min(1).max(40)).max(10).optional(),
  interests: z.array(z.string().min(1).max(40)).max(15).optional(),
  causes: z.array(z.string().min(1).max(40)).max(10).optional(),
  // Social — handle fields enforce socialHandleSchema regex when present
  instagramHandle: socialHandleSchema.nullable().optional(),
  instagramFollowers: z.number().int().nullable().optional(),
  tiktokHandle: socialHandleSchema.nullable().optional(),
  tiktokFollowers: z.number().int().nullable().optional(),
  twitterHandle: socialHandleSchema.nullable().optional(),
  twitterFollowers: z.number().int().nullable().optional(),
  youtubeHandle: socialHandleSchema.nullable().optional(),
  youtubeFollowers: z.number().int().nullable().optional(),
  // Deal preferences — keep loose at the API boundary; enum-narrowed schemas
  // (dealTypeSchema, industrySchema) live in the package root and apply at the
  // form layer when callers want stricter validation.
  preferredDealTypes: z.array(z.string()).optional(),
  preferredIndustries: z.array(z.string()).optional(),
  minimumDealSize: z.number().nullable().optional(),
  openToDeals: z.boolean().optional(),

  // US-031: NCAA eligibility ID. Empty string clears it; any digits-string
  // longer than 0 must validate as exactly 10 digits after normalization.
  // NOTE: only `ncaaId` itself is on this schema. The verification fields
  // (`eligibilityVerifiedAt`, `eligibilityVerifiedBy`,
  // `eligibilityVerificationSource`, `ncaaIdAddedAt`) are deliberately NOT
  // here — they are server-managed (set by the compliance attestation flow
  // and by `updateProfile`'s create-side logic respectively), and exposing
  // them on this self-serve schema would let an athlete self-verify.
  ncaaId: ncaaIdSchema.nullable().optional(),
  onboardingNcaaIdSkippedAt: z.string().datetime().nullable().optional(),
  // What kind of user are you — picked on the first-login onboarding
  // screen. `athlete` (default) → NIL deal tracking + NCAA flows.
  // `staff` → compliance / school / collective / agency / brand;
  // suppresses the athlete-only NCAA prompts.
  // `agent` (2026-07-26) is the sports-agent persona: manages a roster of
  // athletes through an `Organization` with type='agency'. Like `staff` it
  // suppresses the athlete-only NCAA prompts, but it gets its own value so
  // nav and onboarding can route agents to /agency rather than the
  // compliance surfaces staff see.
  userType: z.enum(['athlete', 'staff', 'agent']).optional(),
});

export type UserUpdateInput = z.infer<typeof userUpdateInputSchema>;

// JSON array fields that need serialization for DB storage
const JSON_ARRAY_FIELDS = [
  'personalValues',
  'interests',
  'causes',
  'preferredDealTypes',
  'preferredIndustries',
] as const;

/** Convert API input to Prisma-compatible data (serialize arrays to JSON strings) */
export function serializeJsonArrayFields(input: UserUpdateInput): Record<string, unknown> {
  const data: Record<string, unknown> = { ...input };
  for (const field of JSON_ARRAY_FIELDS) {
    const val = input[field];
    if (val !== undefined) {
      data[field] = val === null ? null : JSON.stringify(val);
    }
  }
  return data;
}
